import { useEffect, useState } from "react";
import { Autocomplete } from "./";
import { useUser } from "../hooks/query";
import { useAuth } from "../hooks";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { offlineStore } from "../utils/offlineStore";

interface User {
    Id: string;
    FirstName: string;
    LastName: string;
    Email: string;
    IsActive?: boolean;
}

interface UserDropdownProps {
    name: string;
    label: string;
    placeholder?: string;
    rules?: object;
    helperText?: string;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    filterActive?: boolean; // Only show active users
}

function UserDropdown({
    name,
    label,
    placeholder = "Select user",
    rules = {},
    helperText,
    size = "md",
    disabled = false,
    filterActive = true, // Default to showing only active users
    ...props
}: UserDropdownProps) {
    const { selectedOrganization } = useAuth();
    const isOnline = useNetworkStatus();
    const { useList } = useUser();
    const { data, isLoading } = useList();
    const [cachedUsers, setCachedUsers] = useState<User[]>([]);

    const orgId = selectedOrganization?.Id;

    useEffect(() => {
        if (!orgId || !Array.isArray(data) || data.length === 0) return;
        offlineStore.saveCachedUsers(orgId, data).catch(console.error);
    }, [orgId, data]);

    useEffect(() => {
        if (!orgId) return;
        if (Array.isArray(data) && data.length > 0) return;
        offlineStore
            .getCachedUsers(orgId)
            .then((users) => setCachedUsers(users as User[]))
            .catch(console.error);
    }, [orgId, data, isOnline]);

    const sourceUsers = (
        Array.isArray(data) && data.length > 0 ? data : cachedUsers
    ) as User[];

    const users = sourceUsers.filter((user) =>
        filterActive ? user.IsActive !== false : true,
    );

    const userOptions = users.map((user) => {
        const fullName = [user.FirstName, user.LastName]
            .filter(Boolean)
            .join(" ")
            .trim();
        return {
            value: user.Email,
            label: fullName || user.Email,
            ...user,
        };
    });

    const loading = isOnline && isLoading && userOptions.length === 0;

    return (
        <Autocomplete
            name={name}
            label={label}
            placeholder={placeholder}
            rules={rules}
            helperText={
                helperText ||
                (!isOnline && userOptions.length === 0
                    ? "Open the app online once to cache salesman options"
                    : undefined)
            }
            size={size}
            options={userOptions}
            disabled={disabled || loading}
            loading={loading}
            isOptionEqualToValue={(option: any, value: any) => {
                if (!option || !value) return false;
                return option.value === value.value;
            }}
            getOptionLabel={(option: any) => {
                if (typeof option === "string") return option;
                return option.label ?? "";
            }}
            {...props}
        />
    );
}

export { UserDropdown };
